import {
  AnimatePresence,
  animate,
  motion,
  useIsPresent,
  useMotionValue,
  usePresence,
  type MotionValue,
  type Transition,
  type ValueAnimationTransition,
} from 'framer-motion'
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { CardHighlight } from '@/Components/custom/card-highlight'
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from '@/Components/ui/drawer'
import { useMediaQuery } from '@/hooks/use-media-query'
import { cn } from '@/lib/utils'

/**
 * A card that opens into itself.
 *
 * There is one shell and everything else is a part given to it.
 *
 * `MorphCards` is the group: which card is open, how an open card is presented
 * (a modal, a drawer, or whichever the breakpoint says), what the pointer does
 * to a card, and how long things take. `MorphCard` is one card: its box, its
 * place in the grid while it is away, and its way home.
 *
 * What a card holds is built from three parts, and how it opens follows from
 * which of them it uses:
 *
 * - `MorphShared` -- a piece both states have. It is the same element in each,
 *   so it travels from one place to the other.
 * - `MorphRest`   -- something only the resting card has.
 * - `MorphOpen`   -- something only the open card has.
 *
 * A card whose states share a header moves those pieces and fades the rest. A
 * card whose states share nothing uses only the last two and its contents
 * change hands inside the travelling box. Neither is a mode of the shell.
 *
 * As a modal the card is one element throughout: its box changes from its
 * place in the grid to the centre of the screen and framer tweens that same
 * node between the two. As a drawer the card stays where it is and a sheet
 * comes up, holding the same parts in their open arrangement.
 *
 * Two things the morph depends on, both outside this file:
 *
 * - What the card holds must be complete on its first render. The box is
 *   measured once, as the state changes; text that arrives a frame later grows
 *   it mid-flight.
 * - No ancestor may be a containing block. A `transform`, `filter` or
 *   `perspective` above the card resolves its `fixed` against that ancestor
 *   instead of the viewport -- and a keyframe's `transform: none` counts.
 */

/**
 * The motion system: Material's container transform.
 *
 * - The surface starts at once, both ways. Nothing waits for anything.
 * - What only the old state has is gone by `through` of the flight; what only
 *   the new state has does not start before it.
 * - Leaving is shorter than arriving.
 * - The curves are for what travels. Opacity is linear across its window: on a
 *   decelerating curve a fade is nine tenths done in its first few frames.
 */
export const MOTION = {
  through: 0.35,
  exit: 0.8,
  ease: {
    emphasized: [0.2, 0, 0, 1],
    decelerate: [0.05, 0.7, 0.1, 1],
    accelerate: [0.3, 0, 0.8, 0.15],
  },
} as const

/** Seconds an outgoing part has before it must be gone. */
export function outgoing(flight: number): number {
  return flight * MOTION.through
}

/* The server has no layout to measure; there it is an ordinary effect. */
const useLayout = typeof window === 'undefined' ? useEffect : useLayoutEffect

function durationOf(transition: Transition): number {
  return 'duration' in transition && typeof transition.duration === 'number'
    ? transition.duration
    : 0.45
}

export type Presentation = 'modal' | 'drawer'

/** `auto` is a modal from the breakpoint up and a drawer below it. */
export type PresentationSetting = Presentation | 'auto'

/** One edge carried between the cards, each card's own, or nothing. */
export type HoverKind = 'magnet' | 'outline' | 'none'

interface GroupValue {
  openId: string | null
  hoveredId: string | null
  presentation: Presentation
  hover: HoverKind
  highlightId: string
  transition: Transition
  open: (id: string) => void
  close: () => void
  setHovered: (id: string | null) => void
}

const GroupContext = createContext<GroupValue | null>(null)

function useGroup(): GroupValue {
  const group = useContext(GroupContext)

  if (!group) {
    throw new Error('<MorphCard> must be rendered inside <MorphCards>.')
  }

  return group
}

const DEFAULT_TRANSITION: Transition = { duration: 0.45, ease: [...MOTION.ease.emphasized] }

/*
 * An open card and what dims the page behind it sit above everything else on
 * the page, the support chat's launcher included, which is at 10000.
 */
const LAYER = { scrim: 'z-[10001]', card: 'z-[10002]' }

interface MorphCardsProps {
  children: ReactNode
  presentation?: PresentationSetting
  /** Where `auto` turns the drawer into a modal. */
  breakpoint?: string
  hover?: HoverKind
  /** The flight open. The flight shut is the same curve, the system's shorter exit. */
  transition?: Transition
  className?: string
}

export function MorphCards({
  children,
  presentation = 'auto',
  breakpoint = '(min-width: 768px)',
  hover = 'magnet',
  transition = DEFAULT_TRANSITION,
  className,
}: MorphCardsProps) {
  const [openId, setOpenId] = useState<string | null>(null)
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const isWide = useMediaQuery(breakpoint)
  const highlightId = useId()
  const grid = useRef<HTMLDivElement>(null)
  const pointer = useRef<{ x: number; y: number } | null>(null)

  const resolved: Presentation =
    presentation === 'auto' ? (isWide ? 'modal' : 'drawer') : presentation

  const open = useCallback((id: string) => {
    setHoveredId(null)
    setOpenId(id)
  }, [])

  const close = useCallback(() => setOpenId(null), [])
  const isModalOpen = openId !== null && resolved === 'modal'

  /* The drawer brings its own scroll lock and its own Escape. */
  useEffect(() => {
    if (!isModalOpen) {
      return
    }

    const { overflow } = document.body.style

    document.body.style.overflow = 'hidden'

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        close()
      }
    }

    window.addEventListener('keydown', onKeyDown)

    return () => {
      document.body.style.overflow = overflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [isModalOpen, close])

  /* Where the pointer is, for the moment a card closes under it. */
  useEffect(() => {
    function onPointerMove(event: PointerEvent) {
      pointer.current = { x: event.clientX, y: event.clientY }
    }

    window.addEventListener('pointermove', onPointerMove, { passive: true })

    return () => window.removeEventListener('pointermove', onPointerMove)
  }, [])

  /*
   * A card closed with the pointer resting over a card gets no event to say
   * so: nothing moved. The card under the pointer is looked up instead -- once
   * the scrim has let go, and again once the closing card is back in its place.
   */
  const landed = durationOf(transition) * MOTION.exit * 1000 + 100

  useEffect(() => {
    if (isModalOpen) {
      return
    }

    function lookUp() {
      const at = pointer.current
      const card = at
        ? document.elementFromPoint(at.x, at.y)?.closest<HTMLElement>('[data-morph-card]')
        : null

      if (card && grid.current?.contains(card)) {
        setHoveredId(card.dataset.morphCard ?? null)
      }
    }

    const frame = requestAnimationFrame(lookUp)
    const timer = setTimeout(lookUp, landed)

    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(timer)
    }
  }, [isModalOpen, landed])

  const value = useMemo<GroupValue>(
    () => ({
      openId,
      hoveredId,
      presentation: resolved,
      hover,
      highlightId,
      transition,
      open,
      close,
      setHovered: setHoveredId,
    }),
    [openId, hoveredId, resolved, hover, highlightId, transition, open, close]
  )

  return (
    <GroupContext.Provider value={value}>
      <div ref={grid} className={className} onMouseLeave={() => setHoveredId(null)}>
        {children}
      </div>

      <AnimatePresence>{isModalOpen ? <Scrim onClick={close} /> : null}</AnimatePresence>
    </GroupContext.Provider>
  )
}

/** What dims the page behind an open card. It stops taking the pointer as it leaves. */
function Scrim({ onClick }: { onClick: () => void }) {
  const isPresent = useIsPresent()

  return (
    <motion.div
      aria-hidden
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: 'linear' }}
      onClick={onClick}
      /*
       * Having this keeps the fade on the main thread. Handed to the
       * compositor, a finished fade lets go a frame before the scrim is
       * removed, and it shows at full strength for that frame.
       */
      onUpdate={() => {}}
      style={{ pointerEvents: isPresent ? 'auto' : 'none' }}
      className={cn('fixed inset-0 bg-black/50 backdrop-blur-[2px]', LAYER.scrim)}
    />
  )
}

interface CardValue {
  /** Whether the parts are in their open arrangement. */
  isOpen: boolean
  /** False inside a drawer, where the parts are simply laid out open. */
  moves: boolean
  close: () => void
  /** Seconds the flight open takes, and the flight shut. */
  enter: number
  leave: number
  /** The flight under way. */
  flight: Transition
}

const CardContext = createContext<CardValue | null>(null)

/** For a part that needs to know where the card is, or to close it. */
export function useMorphCard(): CardValue {
  const card = useContext(CardContext)

  if (!card) {
    throw new Error('A morph part must be rendered inside <MorphCard>.')
  }

  return card
}

const OPEN_BOX =
  'fixed inset-0 m-auto h-fit max-h-[90vh] w-[calc(100%-2rem)] max-w-lg overflow-y-auto'

interface MorphCardProps {
  id: string
  /** Names the open card, and the drawer. */
  label?: string
  /** The surface, in both states. */
  className?: string
  /** The box at rest: its size and padding in the grid. */
  restClassName?: string
  /** The box as a modal, over the centred default: its width and padding. */
  openClassName?: string
  /** The sheet, as a drawer. */
  drawerClassName?: string
  radius?: number
  /** The modal's radius; the corners are tweened to it with the box. */
  openRadius?: number
  children: ReactNode
}

export function MorphCard({
  id,
  label,
  className,
  restClassName,
  openClassName,
  drawerClassName,
  radius = 20,
  openRadius = radius,
  children,
}: MorphCardProps) {
  const group = useGroup()
  const isOpen = group.openId === id
  const isModal = isOpen && group.presentation === 'modal'

  const box = useRef<HTMLDivElement>(null)
  const [slot, setSlot] = useState<number | null>(null)

  const enter = durationOf(group.transition)
  const leave = enter * MOTION.exit
  const flight = useMemo<Transition>(
    () => (isModal ? group.transition : { ...group.transition, duration: leave }),
    [isModal, group.transition, leave]
  )

  /*
   * Still above its neighbours and the fading scrim on the way back. Left to
   * the document order, a card returning to an early cell is painted over by
   * every card after it for half the flight.
   */
  const [landing, setLanding] = useState(false)

  useEffect(() => {
    if (isModal) {
      setLanding(true)
      box.current?.focus({ preventScroll: true })

      return
    }

    const timer = setTimeout(() => setLanding(false), leave * 1000 + 60)

    return () => clearTimeout(timer)
  }, [isModal, leave])

  const openCard = () => {
    /* Taken before the card leaves the flow, so its slot can hold the height. */
    setSlot(box.current?.offsetHeight ?? null)
    group.open(id)
  }

  const atRest = !isOpen && !landing
  const isHovered = group.hoveredId === id && group.openId === null

  const moving = useMemo<CardValue>(
    () => ({ isOpen: isModal, moves: true, close: group.close, enter, leave, flight }),
    [isModal, group.close, enter, leave, flight]
  )

  const sheet = useMemo<CardValue>(
    () => ({ isOpen: true, moves: false, close: group.close, enter, leave, flight }),
    [group.close, enter, leave, flight]
  )

  return (
    <div style={isModal && slot !== null ? { height: slot } : undefined}>
      <motion.div
        ref={box}
        layout
        layoutDependency={isModal}
        initial={false}
        animate={{ borderRadius: isModal ? openRadius : radius }}
        transition={flight}
        role={isModal ? 'dialog' : 'button'}
        aria-modal={isModal ? true : undefined}
        aria-label={label}
        tabIndex={0}
        data-morph-card={id}
        onMouseMove={() => !isOpen && group.hoveredId !== id && group.setHovered(id)}
        onClick={() => !isOpen && openCard()}
        onKeyDown={(event) => {
          if (!isOpen && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault()
            openCard()
          }
        }}
        className={cn(
          'group relative overflow-hidden text-start outline-none [scrollbar-gutter:auto]',
          className,
          isModal ? cn(OPEN_BOX, LAYER.card, openClassName) : cn('cursor-pointer', restClassName),
          !isModal && landing && LAYER.card
        )}
      >
        {atRest ? (
          <HoverEdge
            kind={group.hover}
            isHovered={isHovered}
            highlightId={group.highlightId}
            radius={radius}
          />
        ) : null}

        <CardContext.Provider value={moving}>{children}</CardContext.Provider>
      </motion.div>

      <Drawer
        open={isOpen && group.presentation === 'drawer'}
        onOpenChange={(next) => !next && group.close()}
      >
        <DrawerContent
          overlayClassName={LAYER.scrim}
          className={cn('max-h-[92dvh] outline-none', LAYER.card, drawerClassName)}
        >
          <DrawerTitle className="sr-only">{label}</DrawerTitle>
          <DrawerDescription className="sr-only">{label}</DrawerDescription>
          {/* The sheet stops short of the screen; what does not fit scrolls inside it. */}
          <div className="min-h-0 overflow-y-auto">
            <CardContext.Provider value={sheet}>{children}</CardContext.Provider>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  )
}

function HoverEdge({
  kind,
  isHovered,
  highlightId,
  radius,
}: {
  kind: HoverKind
  isHovered: boolean
  highlightId: string
  radius: number
}) {
  if (kind === 'magnet') {
    return isHovered ? <CardHighlight id={highlightId} radius={radius} className="z-20" /> : null
  }

  if (kind === 'outline') {
    return (
      <span
        aria-hidden
        style={{ borderRadius: radius }}
        className="pointer-events-none absolute inset-0 z-20 opacity-0 ring-2 ring-inset ring-[hsl(var(--foreground)/0.07)] transition-opacity duration-300 group-hover:opacity-100 dark:ring-primary/25"
      />
    )
  }

  return null
}

/**
 * A piece both states have.
 *
 * It is the same element at rest and open, so when the card's layout changes
 * it is carried from where it was to where it now belongs. By default it keeps
 * its size and only moves, which suits text; `resize` lets it change size on
 * the way, which suits a picture or a figure set larger once open.
 */
export function MorphShared({
  as = 'div',
  resize = false,
  className,
  style,
  children,
  ...data
}: {
  as?: 'div' | 'span'
  resize?: boolean
  className?: string
  style?: CSSProperties
  children?: ReactNode
  [key: `data-${string}`]: string
}) {
  const card = useMorphCard()
  const part = useRef<HTMLElement>(null)
  const x = useLeadingEdge(part, card, !resize)

  if (!card.moves) {
    return createElement(as, { className, style, ...data }, children)
  }

  const Part = as === 'span' ? motion.span : motion.div

  return (
    <Part
      ref={part as never}
      layout={resize ? true : 'position'}
      layoutDependency={card.isOpen}
      transition={card.flight}
      className={className}
      style={{ ...style, x }}
      {...data}
    >
      {children}
    </Part>
  )
}

/**
 * Keeps a piece's leading edge where it belongs when it changes width.
 *
 * A piece that only moves is pinned by its left edge: that is the corner framer
 * carries, and whatever the piece gains in width it gains to the right. In a
 * left-to-right layout that is its trailing edge and nobody sees it. In a
 * right-to-left one the text starts on the right, so a name that is no longer
 * truncated is thrown sideways by its own growth and slides back. This takes
 * the difference out again, on the same clock as the flight.
 */
function useLeadingEdge(
  part: { current: HTMLElement | null },
  card: CardValue,
  applies: boolean
): MotionValue<number> {
  const x = useMotionValue(0)
  const width = useRef<number | null>(null)

  useLayout(() => {
    const node = part.current

    if (!node || !applies || !card.moves) {
      return
    }

    const before = width.current
    const after = node.offsetWidth

    width.current = after

    if (before === null || before === after || getComputedStyle(node).direction !== 'rtl') {
      return
    }

    const settle = animate(x, [before - after, 0], card.flight as ValueAnimationTransition<number>)

    return () => settle.stop()
  }, [card.isOpen])

  return x
}

type Arriving = (present: boolean) => ReactNode

interface StateOnlyProps {
  className?: string
  /**
   * Fade as one. Turn off for a part that arrives in its own order -- rows a
   * step apart -- and give it a function, which is told when it is on its way out.
   */
  fade?: boolean
  children: ReactNode | Arriving
}

/** Something only the resting card has. */
export function MorphRest(props: StateOnlyProps) {
  return <StateOnly of="rest" {...props} />
}

/** Something only the open card has. */
export function MorphOpen(props: StateOnlyProps) {
  return <StateOnly of="open" {...props} />
}

function StateOnly({
  of,
  className,
  fade = true,
  children,
}: StateOnlyProps & { of: 'rest' | 'open' }) {
  const card = useMorphCard()
  const wanted = of === 'open' ? card.isOpen : !card.isOpen
  const render = (present: boolean) =>
    typeof children === 'function' ? children(present) : children

  if (!card.moves) {
    return wanted ? <div className={className}>{render(true)}</div> : null
  }

  /* The rest leaves on the way open and returns on the shorter way shut. */
  const timing =
    of === 'open'
      ? { enter: card.enter, leave: card.leave }
      : { enter: card.leave, leave: card.enter }

  return (
    <AnimatePresence initial={false}>
      {wanted ? (
        <Held key={of} timing={timing} fade={fade} transition={card.flight} className={className}>
          {render}
        </Held>
      ) : null}
    </AnimatePresence>
  )
}

/**
 * While leaving, a state-only part is lifted out of the flow and pinned where
 * it stood, at the size it had, for as long as it takes to go. The card is
 * already changing shape by then, and without this the new shape would re-wrap
 * or re-crop it on the first frame.
 */
function Held({
  children,
  timing,
  fade,
  transition,
  className,
}: {
  children: Arriving
  timing: { enter: number; leave: number }
  fade: boolean
  transition: Transition
  className?: string
}) {
  const [isPresent, safeToRemove] = usePresence()
  const node = useRef<HTMLDivElement>(null)
  const pin = useRef<{
    top: number
    insetInlineStart: number
    width: number
    height: number
  } | null>(null)

  useLayout(() => {
    const element = node.current
    const parent = element?.offsetParent

    if (!isPresent || !element || !(parent instanceof HTMLElement)) {
      return
    }

    const isRtl = getComputedStyle(parent).direction === 'rtl'

    pin.current = {
      top: element.offsetTop,
      insetInlineStart: isRtl
        ? parent.clientWidth - element.offsetLeft - element.offsetWidth
        : element.offsetLeft,
      width: element.offsetWidth,
      height: element.offsetHeight,
    }
  })

  useEffect(() => {
    if (isPresent) {
      return
    }

    const timer = setTimeout(() => safeToRemove?.(), outgoing(timing.leave) * 1000 + 20)

    return () => clearTimeout(timer)
  }, [isPresent, timing.leave, safeToRemove])

  const opacity = isPresent
    ? { duration: timing.enter * 0.5, delay: outgoing(timing.enter), ease: 'linear' as const }
    : { duration: outgoing(timing.leave), ease: 'linear' as const }

  return (
    <motion.div
      ref={node}
      layout="position"
      initial={fade ? { opacity: 0 } : false}
      animate={fade ? { opacity: isPresent ? 1 : 0 } : undefined}
      transition={{ ...transition, opacity }}
      style={!isPresent && pin.current ? { position: 'absolute', ...pin.current } : undefined}
      className={className}
    >
      {children(isPresent)}
    </motion.div>
  )
}

/**
 * A fade on the system's clock, for a part that composes its own arrival.
 * `shown` is what the part was told; the timing comes from the card.
 */
export function MorphFade({
  shown,
  className,
  children,
}: {
  shown: boolean
  className?: string
  children: ReactNode
}) {
  const { enter, leave, moves } = useMorphCard()

  if (!moves) {
    return <div className={className}>{children}</div>
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: shown ? 1 : 0 }}
      transition={
        shown
          ? { duration: enter * 0.5, delay: outgoing(enter), ease: 'linear' }
          : { duration: outgoing(leave), ease: 'linear' }
      }
      className={className}
    >
      {children}
    </motion.div>
  )
}
